import { AuthStore, User } from "@infrastructure/stores/auth.store";
import { AuthClient } from "@infrastructure/clients/http/clients/auth.client";
import { UserClient } from "@infrastructure/clients/http/clients/user.client";
import { authToken } from "@infrastructure/clients/http/authToken";
import { catchError } from "@shared/utils/errors/catchError";
import { ApiError } from "@shared/utils/errors/apiError";
import { toast } from "sonner";
import { hideSplashScreen } from "@shared/utils/splashScreen";
import { PromoService } from "@domain/services/promo.service";
import { getFreshInitDataRaw } from "@shared/integrations/telegram/init";
import { applyDemoLoginFromUrl, isDemoSession } from "@shared/integrations/demoLogin";

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, "");

  if (cleaned.startsWith("+7")) {
    return cleaned;
  }

  if (cleaned.startsWith("7") || cleaned.startsWith("8")) {
    return "+7" + cleaned.slice(1);
  }

  return "+7" + cleaned;
}

class AuthServiceClass {
  private reauthPromise: Promise<boolean> | null = null;
  private isReauthing = false;
  private backgroundReauthSubscription: (() => void) | null = null;
  private backgroundReauthInterval: number | null = null;

  /**
   * Получает свежий initDataRaw из Telegram WebApp
   * Всегда получает актуальный токен, а не использует закэшированный
   */
  private getFreshInitData(): string | null {
    const freshToken = getFreshInitDataRaw();
    console.log(`[AuthService.getFreshInitData] Got token: ${freshToken ? `yes (length: ${freshToken.length})` : 'null'}`);
    if (freshToken) {
      // Обновляем кэш для совместимости со старым кодом
      AuthStore.initDataRaw = freshToken;
    }
    return freshToken;
  }
  private initPromise: Promise<void> | null = null;
  private registrationCheckCache: { result: boolean; timestamp: number } | null = null;
  private validatePromise: Promise<any> | null = null;

  getStore() {
    return AuthStore;
  }

  /**
   * Запускает фоновую переавторизацию (не блокирует выполнение)
   * Используется для автоматической переавторизации при истечении токена
   */
  async startBackgroundReauth(): Promise<void> {
    // Если уже идет процесс переавторизации, не запускаем новый
    if (this.reauthPromise) {
      return;
    }

    // Запускаем переавторизацию в фоне
    this.reauth().catch((err) => {
      console.error("[AuthService.startBackgroundReauth] Background reauth failed:", err);
    });
  }

  /**
   * Инициализирует глобальный механизм фоновой переавторизации
   * Отслеживает состояние reauthRequired и автоматически запускает переавторизацию
   */
  initBackgroundReauth(): void {
    // Останавливаем предыдущую подписку, если она была
    this.stopBackgroundReauth();

    // Проверяем текущее состояние и запускаем переавторизацию, если нужно
    const currentState = AuthStore.getState();
    if (
      currentState.auth.state === "anonymous" &&
      currentState.auth.reauthRequired &&
      !this.reauthPromise &&
      AuthStore.initDataRaw
    ) {
      this.startBackgroundReauth();
    }

    // Подписываемся на изменения состояния авторизации
    // При установке reauthRequired автоматически запускается переавторизация
    this.backgroundReauthSubscription = AuthStore.subscribe(() => {
      const state = AuthStore.getState();
      const authState = state.auth;

      // Если требуется переавторизация и мы не авторизованы, запускаем фоновую переавторизацию
      if (
        authState.state === "anonymous" &&
        authState.reauthRequired &&
        !this.reauthPromise &&
        AuthStore.initDataRaw
      ) {
        this.startBackgroundReauth();
      }
    });
  }

  /**
   * Останавливает механизм фоновой переавторизации
   */
  stopBackgroundReauth(): void {
    if (this.backgroundReauthSubscription) {
      this.backgroundReauthSubscription();
      this.backgroundReauthSubscription = null;
    }

    if (this.backgroundReauthInterval !== null) {
      clearInterval(this.backgroundReauthInterval);
      this.backgroundReauthInterval = null;
    }
  }

  /**
   * Автоматическая переавторизация при истечении токена
   * Предотвращает множественные одновременные попытки переавторизации
   */
  async reauth(): Promise<boolean> {
    // Если уже идет процесс переавторизации, ждем его завершения
    if (this.reauthPromise) {
      console.log("[AuthService.reauth] Re-auth already in progress, waiting...");
      return this.reauthPromise;
    }

    // Если уже авторизованы, не нужно переавторизовываться
    if (AuthStore.auth.state === "authenticated") {
      console.log("[AuthService.reauth] Already authenticated, skipping re-auth");
      return true;
    }

    console.log("[AuthService.reauth] Starting reauth process, current state:", AuthStore.auth.state);

    // Показ механики: подписи мессенджера нет, зато есть пропуск. Токен живёт
    // полчаса, и без этой ветки показ обрывался бы на середине — приложение
    // выкидывало бы человека из вошедших ровно тогда, когда его показывают.
    if (isDemoSession()) {
      const restored = await applyDemoLoginFromUrl();
      if (restored) {
        await this.getUser();
        console.log("[AuthService.reauth] Вход по пропуску продлён");
        return true;
      }
      console.error("[AuthService.reauth] Пропуск показа не сработал");
      this.reauthPromise = null;
      return false;
    }

    // Всегда получаем свежий токен из Telegram WebApp
    const initDataRaw = this.getFreshInitData();
    if (!initDataRaw) {
      console.error("[AuthService.reauth] Failed to get fresh initDataRaw from Telegram");
      this.reauthPromise = null;
      return false;
    }
    console.log("[AuthService.reauth] Got fresh initDataRaw, length:", initDataRaw.length);

    // Проверяем, что пользователь был зарегистрирован
    const authClient = new AuthClient();
    console.log("[AuthService.reauth] Validating initDataRaw...");
    const [validateError, validateData] = await catchError(
      authClient.validate(initDataRaw)
    );

    if (validateError) {
      console.error("[AuthService.reauth] Validate error:", validateError);
      this.reauthPromise = null;
      return false;
    }

    const { is_register, is_phone_number } = validateData.data;
    console.log("[AuthService.reauth] Validate result: is_register=", is_register, "is_phone_number=", is_phone_number);
    
    if (!is_register || !is_phone_number) {
      console.error("[AuthService.reauth] User not registered or no phone number");
      this.reauthPromise = null;
      return false;
    }

    // Создаем промис переавторизации
    this.reauthPromise = (async () => {
      try {
        this.isReauthing = true;
        
        const authClient = new AuthClient();
        const [loginError, loginData] = await catchError(
          authClient.signin(initDataRaw)
        );

        if (loginError) {
          console.error("[AuthService.reauth] Signin error:", loginError);
          AuthStore.setAuth({ state: "anonymous", reauthRequired: true });
          return false;
        }

        console.log("[AuthService.reauth] Re-authentication successful, setting token");
        // ВАЖНО: Удаляем старый токен перед установкой нового
        authToken.remove();
        authToken.set(loginData.data.token);
        await this.getUser();
        console.log("[AuthService.reauth] User re-authenticated successfully");
        return true;
      } finally {
        this.isReauthing = false;
        this.reauthPromise = null;
      }
    })();

    return this.reauthPromise;
  }

  async init() {
    // Если инициализация уже идет, ждем её завершения
    if (this.initPromise) {
      console.log("[AuthService.init] Initialization already in progress, waiting...");
      return this.initPromise;
    }
    
    console.log("[AuthService.init] Starting initialization...");
    
    this.initPromise = (async () => {
      // Показ механики: человек вошёл по пропуску, подписи мессенджера у него нет
      // и быть не может. Токен уже получен и лежит на месте — сразу забираем
      // человека и считаем его вошедшим, иначе приложение увело бы его на «О нас».
      if (isDemoSession()) {
        console.log("[AuthService.init] Вход по пропуску показа");
        await this.getUser();
        return;
      }

      // Всегда получаем свежий токен из Telegram WebApp
      const freshToken = this.getFreshInitData();
      if (!freshToken) {
        console.log("[AuthService.init] No initDataRaw, setting anonymous state");
        AuthStore.setAuth({ state: "anonymous", reauthRequired: false });
        return;
      }
      
      // Дедупликация: если запрос validate уже идет (например, из checkUserRegistrationStatus), используем его
      let validatePromise: Promise<any>;
      if (this.validatePromise) {
        console.log("[AuthService.init] Validate request already in progress, reusing...");
        validatePromise = this.validatePromise;
      } else {
        const authClient = new AuthClient();
        // Используем свежий токен
        console.log(`[AuthService.init] Sending validate request with token length: ${freshToken.length}`);
        this.validatePromise = authClient.validate(freshToken);
        validatePromise = this.validatePromise;
      }
      
      const [validateError, validateData] = await catchError(validatePromise);
      
      // Очищаем промис после завершения
      this.validatePromise = null;

      if (validateError) {
        console.error("[AuthService.init] Telegram validate error:", validateError);
        hideSplashScreen();

        // Проверяем, не истек ли initData токен
        const isExpiredError = validateError instanceof ApiError && 
          (validateError.message?.includes("initData expired") || 
           validateError.message?.includes("expired") ||
           (validateError as any).details?.includes("initData expired"));
        
        if (isExpiredError) {
          console.warn("[AuthService.init] InitData expired, reloading page to get fresh token");
          toast.error("Токен авторизации истек", {
            description: "Перезагружаем страницу...",
            duration: 2000,
          });
          // Перезагружаем страницу через небольшую задержку
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          return;
        }

        if (validateError instanceof ApiError) {
          toast.error("Ошибка авторизации", {
            description: validateError.message || "Пожалуйста, напишите в поддержку",
          });
        } else {
          toast.error("Произошла ошибка", {
            description: "Пожалуйста, напишите в поддержку",
          });
        }

        console.log("[AuthService.init] Setting auth state to anonymous (no reauth)");
        AuthStore.setAuth({ state: "anonymous", reauthRequired: false });
        return;
      }

      // Проверяем, зарегистрирован ли пользователь и есть ли номер телефона
      const { is_register, is_phone_number } = validateData.data;
      console.log(`[AuthService.init] Validate result: is_register=${is_register}, is_phone_number=${is_phone_number}`);

    // Если пользователь зарегистрирован и есть номер телефона - делаем signin
    if (is_register && is_phone_number) {
      const authClient = new AuthClient();
      // Всегда получаем свежий токен из Telegram WebApp
      const freshToken = this.getFreshInitData();
      if (!freshToken) {
        throw new Error("Failed to get fresh initDataRaw from Telegram");
      }
      const [loginError, loginData] = await catchError(
        authClient.signin(freshToken)
      );

      if (loginError) {
        console.error("[AuthService.init] Signin error:", loginError);
        console.log("[AuthService.init] Setting auth state to anonymous (reauth required)");
        AuthStore.setAuth({ state: "anonymous", reauthRequired: true });
        // Запускаем фоновую переавторизацию автоматически
        this.startBackgroundReauth().catch((err) => {
          console.error("[AuthService.init] Background reauth failed:", err);
        });
        return;
      }

      console.log("[AuthService.init] Signin successful, setting token and fetching user");
      // ВАЖНО: Удаляем старый токен перед установкой нового
      authToken.remove();
      authToken.set(loginData.data.token);
      await this.getUser();
      console.log("[AuthService.init] User authenticated successfully");
    } else {
      // Если пользователь не зарегистрирован или нет номера телефона - переходим на /about
      console.log("[AuthService.init] User not registered or no phone, setting anonymous without reauth");
      AuthStore.setAuth({ state: "anonymous", reauthRequired: false });
    }
    })();

    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async getUser() {
    const userClient = new UserClient();
    const [_error, user] = await catchError(userClient.getCurrentUser());

    const userData = user
      ? user.data
      : {
          id: 0,
          language: "ru",
          phone_number: null,
          name: null,
          photo: null,
        };

    console.log(`[AuthService.getUser] User data fetched: id=${userData.id}, name=${userData.name || 'null'}`);
    AuthStore.setAuth({
      state: "authenticated",
      user: userData,
    });
  }

  async refreshUser() {
    if (AuthStore.auth.state !== "authenticated") return;
    await this.getUser();
  }

  async login(phone: string, code: string): Promise<boolean> {
    // ВАЖНО: Удаляем старый токен перед логином нового пользователя
    // Это предотвращает привязку карты к неправильному пользователю
    authToken.remove();
    
    // Всегда получаем свежий токен из Telegram WebApp
    const freshToken = this.getFreshInitData();
    if (!freshToken) {
      toast.error("Ошибка получения данных Telegram");
      return false;
    }
    
    const authClient = new AuthClient();
    const [error, data] = await catchError(
      authClient.verify(
        freshToken,
        formatPhoneNumber(phone),
        code
      )
    );

    if (error) {
      if (error instanceof ApiError) {
        error.showError();
      } else {
        toast.error("Неверный код");
      }
      return false;
    }

    authToken.set(data.data.token);
    await this.getUser();
    return true;
  }

  async register(phone: string) {
    // Всегда получаем свежий токен из Telegram WebApp
    const freshToken = this.getFreshInitData();
    if (!freshToken) {
      throw new Error("Failed to get fresh initDataRaw from Telegram");
    }
    
    const authClient = new AuthClient();
    return authClient.register(
      freshToken,
      formatPhoneNumber(phone)
    );
  }

  async updateUserInfo(userInfo: Partial<Pick<User, "name" | "photo" | "language">>) {
    if (AuthStore.auth.state !== "authenticated") return;

    const userClient = new UserClient();
    const newUserInfo = await userClient.updateUser(userInfo);
    // Обновляем только измененные поля пользователя
    if (AuthStore.auth.state === "authenticated") {
      const currentUser = AuthStore.auth.user;
      AuthStore.setAuth({
        state: "authenticated",
        user: {
          ...currentUser,
          ...newUserInfo.data
        }
      });
    }
  }

  logout() {
    authToken.remove();
    AuthStore.setAuth({ state: "anonymous", reauthRequired: false });
  }

  /**
   * Получает текущий токен авторизации
   */
  getToken(): string | null {
    return authToken.get();
  }

  /**
   * Устанавливает токен авторизации
   */
  setToken(token: string | null | undefined): void {
    authToken.set(token);
  }

  /**
   * Проверяет, зарегистрирован ли пользователь и есть ли у него номер телефона
   * Возвращает true если пользователь полностью зарегистрирован (is_register: true и is_phone_number: true)
   * Кэширует результат на 5 секунд для избежания множественных запросов
   */
  async checkUserRegistrationStatus(): Promise<boolean> {
    // Проверяем кэш (валидность 5 секунд)
    const now = Date.now();
    if (this.registrationCheckCache && (now - this.registrationCheckCache.timestamp) < 5000) {
      return this.registrationCheckCache.result;
    }
    
    // На показе подписи мессенджера нет, а человек заведён и с телефоном: спросить
    // о нём можно только по токену. Иначе приложение считало бы его
    // недорегистрированным и не пускало дальше экрана согласий.
    if (isDemoSession()) {
      const registered = AuthStore.auth.state === "authenticated";
      this.registrationCheckCache = { result: registered, timestamp: now };
      return registered;
    }

    // Всегда получаем свежий токен из Telegram WebApp
    const freshToken = this.getFreshInitData();
    if (!freshToken) {
      this.registrationCheckCache = { result: false, timestamp: now };
      return false;
    }

    try {
      // Дедупликация: если запрос validate уже идет, ждем его завершения
      let validatePromise: Promise<any>;
      if (this.validatePromise) {
        validatePromise = this.validatePromise;
      } else {
        const authClient = new AuthClient();
        // Используем свежий токен
        this.validatePromise = authClient.validate(freshToken);
        validatePromise = this.validatePromise;
      }
      
      const [validateError, validateData] = await catchError(validatePromise);
      
      // Очищаем промис после завершения (успешного или с ошибкой)
      if (this.validatePromise === validatePromise) {
        this.validatePromise = null;
      }

      if (validateError) {
        // Проверяем, не истек ли initData токен
        // Проверяем в message (который содержит details из API ответа)
        const errorMessage = validateError instanceof Error 
          ? validateError.message 
          : String(validateError);
        
        const isExpiredError = errorMessage.includes("initData expired") || 
                               errorMessage.includes("expired");
        
        if (isExpiredError) {
          console.warn("[AuthService.checkUserRegistrationStatus] InitData expired, reloading page to get fresh token");
          toast.error("Токен авторизации истек", {
            description: "Перезагружаем страницу...",
            duration: 2000,
          });
          // Перезагружаем страницу через небольшую задержку
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          this.registrationCheckCache = { result: false, timestamp: now };
          return false;
        }
        
        // Если ошибка связана с отменой/таймаутом, не кешируем результат
        const isTimeoutError = validateError instanceof Error && 
          (validateError.message.includes('время ожидания') || 
           validateError.message.includes('aborted') || 
           validateError.message.includes('canceled'));
        if (!isTimeoutError) {
          this.registrationCheckCache = { result: false, timestamp: now };
        }
        return false;
      }

      const { is_register, is_phone_number } = validateData.data;
      const result = is_register && is_phone_number;
      
      // Кэшируем результат
      this.registrationCheckCache = { result, timestamp: now };
      return result;
    } catch (error) {
      console.error("[AuthService.checkUserRegistrationStatus] Error:", error);
      this.registrationCheckCache = { result: false, timestamp: now };
      return false;
    }
  }

  /**
   * Удаляет токен авторизации
   */
  removeToken(): void {
    authToken.remove();
  }
}

export const AuthService = new AuthServiceClass();

